import apiClient from './apiClient';

export interface ExportOptions {
  scope: 'full' | 'identity' | 'medical' | 'visits' | 'vaccinations' | 'prescriptions' | 'documents';
  from?: string;
  to?: string;
  includeIdentity?: boolean;
  includeActiveProblems?: boolean;
  includeVisits?: boolean;
  includeVaccinations?: boolean;
  includePrescriptions?: boolean;
  includeDocuments?: boolean;
  includeNotes?: boolean;
}

export interface ExportSections {
  identity: boolean;
  problems: boolean;
  visits: boolean;
  vaccinations: boolean;
  prescriptions: boolean;
  documents: boolean;
  notes: boolean;
}

export interface ExportPDFOptions {
  petId: string;
  clinicId?: string;
  timeInterval: 'all' | 'last_year' | 'last_6_months' | 'last_3_months' | 'custom';
  fromDate?: Date | null;
  toDate?: Date | null;
  sections: ExportSections;
  includeImages: boolean;
  includeOCRText: boolean;
  language: 'ro' | 'en';
}

class ExportService {
  async exportPetPDF(petId: string, options: ExportOptions): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      
      if (options.scope) params.append('scope', options.scope);
      if (options.from) params.append('from', options.from);
      if (options.to) params.append('to', options.to);
      if (options.includeIdentity !== undefined) params.append('includeIdentity', options.includeIdentity.toString());
      if (options.includeActiveProblems !== undefined) params.append('includeActiveProblems', options.includeActiveProblems.toString());
      if (options.includeVisits !== undefined) params.append('includeVisits', options.includeVisits.toString());
      if (options.includeVaccinations !== undefined) params.append('includeVaccinations', options.includeVaccinations.toString());
      if (options.includePrescriptions !== undefined) params.append('includePrescriptions', options.includePrescriptions.toString());
      if (options.includeDocuments !== undefined) params.append('includeDocuments', options.includeDocuments.toString());
      if (options.includeNotes !== undefined) params.append('includeNotes', options.includeNotes.toString());

      const response = await apiClient.get(`/pets/${petId}/print?${params}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      console.error('Error exporting PDF:', error);
      throw error;
    }
  }

  async downloadPDF(blob: Blob, fileName: string): Promise<void> {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }

  async exportAnimalRecord(options: ExportPDFOptions): Promise<void> {
    try {
      const exportData = {
        ...options,
        fromDate: options.fromDate?.toISOString(),
        toDate: options.toDate?.toISOString()
      };

      const response = await apiClient.post(`/pets/${options.petId}/export-pdf`, exportData, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fisa_${options.petId}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting animal record:', error);
      throw error;
    }
  }
}

export const exportService = new ExportService();
